import { useCallback, useMemo } from 'react';
import { microServicesRoutes } from '@constants';
import { contactsApi, edgeApi, syncApi } from '@lib/apiClients';
import { logError } from '@lib/logger/logError';
import { logInfo } from '@lib/logger/logInfo';
import { extractErrorMessage } from '@lib/logger/extractErrorMessage';

import type {
  ApiResponse,
  BuildMetrics,
  ContactInfo,
  DashboardFilters,
  DashboardSession,
  DispositionMetrics,
  IntegrationJob,
  Job,
  OrganizationInfo,
  PaginatedResponse, PaginatedWithMetricsResponse,
  SdrInfo,
  SessionDetails,
  SessionRecordings,
} from '../types/dashboard.types';
import type { OverviewQueryParams } from '@types';
import { logWarning } from '@lib/logger/logWarning.ts';
import { useTranslation } from 'react-i18next';

export type ReportType = 'raw' | 'compact';

// Module-scoped dedupe for getOrganizationsAsync: multiple callers (filter bar,
// session enrichment) on the same page would otherwise each issue a GET /organization.
// The list rarely changes, so cache the resolved response for 5 minutes.
const ORG_CACHE_TTL_MS = 5 * 60 * 1000;
let orgCache: { fetchedAt: number; data: OrganizationInfo[] } | null = null;
let pendingOrgsPromise: Promise<ApiResponse<OrganizationInfo[]>> | null = null;

export interface UseDashboardApiReturn {
  getSessionsAsync: (
    userId: string,
    filters: DashboardFilters,
    page: number,
    pageLimit: number,
    sdrIds?: string[],
    orgIds?: string[],
  ) => Promise<ApiResponse<PaginatedResponse<DashboardSession>>>;
  getSessionDetailsAsync: (
    callSessionId: string,
    userId: string,
    page: number,
    limit: number,
  ) => Promise<ApiResponse<PaginatedResponse<SessionDetails>>>;
  getRecordingsAsync: (
    callId: string,
    userId: string,
  ) => Promise<ApiResponse<SessionRecordings>>;
  getSdrsAsync: (
    userId: string,
    userIds: string[] | null,
    organizationId: string | null,
  ) => Promise<ApiResponse<SdrInfo[]>>;
  getOrganizationsAsync: () => Promise<ApiResponse<OrganizationInfo[]>>;
  getContactByIdAsync: (contactId: string) => Promise<ApiResponse<ContactInfo>>;
  downloadReportAsync: (
    callSessionId: string,
    reportType: ReportType,
    organizationId?: string,
  ) => Promise<ApiResponse<undefined>>;
  getBuildMetricsAsync: (callSessionId: string) => Promise<ApiResponse<BuildMetrics>>;
  getDispositionMetricsAsync: (callSessionId: string) => Promise<ApiResponse<DispositionMetrics>>;
  getOverviewKpisAsync: (params: OverviewQueryParams) => Promise<ApiResponse<unknown>>;
  getOverviewLeaderboardAsync: (params: OverviewQueryParams) => Promise<ApiResponse<unknown>>;
  getHubspotJobs: (
      organizationId: string,
      filters: DashboardFilters,
      page: string,
      pageLimit: string,
  ) => Promise<ApiResponse<PaginatedWithMetricsResponse<IntegrationJob>>>;
  getOutreachJobs: (
      organizationId: string,
      filters: DashboardFilters,
      page: string,
      pageLimit: string,
  ) => Promise<ApiResponse<PaginatedWithMetricsResponse<IntegrationJob>>>;
  getSalesforceJobs: (
      organizationId: string,
      filters: DashboardFilters,
      page: string,
      pageLimit: string,
  ) => Promise<ApiResponse<PaginatedWithMetricsResponse<IntegrationJob>>>;
  retryHubspotJob: (
      jobId: string,
      includeSkipped?: boolean,
  ) => Promise<ApiResponse<Job>>;
  retryOutreachJob: (
      jobId: string,
  ) => Promise<ApiResponse<Job>>;
  retrySalesforceJob: (
      jobId: string,
  ) => Promise<ApiResponse<Job>>;
}

export default function useDashboardApi (): UseDashboardApiReturn {
  const { t } = useTranslation();

  const handleError = useCallback(<T = never>(
    message: string,
    error: unknown,
    source: string,
    context?: Record<string, unknown>,
  ): ApiResponse<T> => {
    const errorMessage = extractErrorMessage(error);

    if (extractErrorMessage(error).includes('404')) {
      void logWarning(t('dashboard.useDashboardApi.withoutDetails', 'Without session details.'), source, {
        ...context,
        error: errorMessage,
        rawError: error,
      }, false);
    } else {
      void logError(message, source, {
        ...context,
        error: errorMessage,
        rawError: error,
      }, false);
    }

    return { success: false, error: { message, raw: errorMessage } };
  }, []);

  const getSessionsAsync = useCallback(async (
    userId: string,
    filters: DashboardFilters,
    page: number,
    pageLimit: number,
    sdrIds?: string[],
    orgIds?: string[],
  ): Promise<ApiResponse<PaginatedResponse<DashboardSession>>> => {
    const source = 'Dashboard.getSessionsAsync';
    try {
      const params: Record<string, string | number> = {
        page,
        pageLimit,
      };

      if (filters.searchQuery !== '') {
        params.searchQuery = filters.searchQuery;
      }

      // Add date filters (new v3 params)
      // Handle both Date objects and ISO strings (from persisted store)
      if (filters.dateRange.from !== undefined && filters.dateRange.from !== null) {
        const fromDate = filters.dateRange.from instanceof Date
          ? filters.dateRange.from
          : new Date(filters.dateRange.from);
        params.dateFrom = fromDate.toISOString();
      }
      if (filters.dateRange.to !== undefined && filters.dateRange.to !== null) {
        const toDate = filters.dateRange.to instanceof Date
          ? filters.dateRange.to
          : new Date(filters.dateRange.to);
        params.dateTo = toDate.toISOString();
      }

      const requestUrl = `${microServicesRoutes.contacts.get.dashboard.getSessions}/${userId}`;

      const response = await contactsApi.post<PaginatedResponse<DashboardSession>>(
        requestUrl,
        {
          sdrIds: sdrIds ?? filters.sdrIds,
          orgIds: orgIds ?? filters.orgIds ?? [],
          listTypes: filters.listTypes.length > 0 ? filters.listTypes : undefined,
        },
        { params },
      );

      void logInfo('Dashboard sessions fetched.', source, {
        userId,
        page,
        totalCount: response.data?.totalCount,
        dateFrom: filters.dateRange.from,
        dateTo: filters.dateRange.to,
      });

      return {
        success: true,
        data: response.data,
        totalPages: response.data?.totalPages,
        totalCount: response.data?.totalCount,
        pageNumber: response.data?.pageNumber,
      };
    } catch (error) {
      return handleError<PaginatedResponse<DashboardSession>>(
        'Error fetching dashboard sessions.',
        error,
        source,
        { userId, page, pageLimit },
      );
    }
  }, [handleError]);

  const getSessionDetailsAsync = useCallback(async (
    callSessionId: string,
    userId: string,
    page: number,
    limit: number,
  ): Promise<ApiResponse<PaginatedResponse<SessionDetails>>> => {
    const source = 'Dashboard.getSessionDetailsAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.get.dashboard.getSessionDetails}/${callSessionId}/user/${userId}`;

      const response = await contactsApi.get<PaginatedResponse<SessionDetails>>(
        requestUrl,
        { params: { page, limit } },
      );

      void logInfo('Session details fetched.', source, {
        callSessionId,
        userId,
        page,
      });

      return {
        success: true,
        data: response.data,
        totalPages: response.data?.totalPages,
        totalCount: response.data?.totalCount,
        pageNumber: response.data?.pageNumber,
      };
    } catch (error) {
      return handleError<PaginatedResponse<SessionDetails>>(
        'Error fetching session details.',
        error,
        source,
        { callSessionId, userId },
      );
    }
  }, [handleError]);

  const getRecordingsAsync = useCallback(async (
    callId: string,
    userId: string,
  ): Promise<ApiResponse<SessionRecordings>> => {
    const source = 'Dashboard.getRecordingsAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.get.dashboard.getRecordings}/${callId}/user/${userId}`;

      const response = await contactsApi.get<SessionRecordings>(requestUrl);

      void logInfo('Recordings fetched.', source, { callId, userId });

      return { success: true, data: response.data };
    } catch (error) {
      return handleError<SessionRecordings>(
        'Error fetching recordings.',
        error,
        source,
        { callId, userId },
      );
    }
  }, [handleError]);

  const getSdrsAsync = useCallback(async (
    userId: string,
    userIds: string[] | null,
    organizationId: string | null,
  ): Promise<ApiResponse<SdrInfo[]>> => {
    const source = 'Dashboard.getSdrsAsync';
    try {
      const { data } = await edgeApi.get<SdrInfo[]>(
        `${microServicesRoutes.edge.get.users.allActiveSdrs}/${userId}`,
        {
          params: {
            userIds,
            organizationId,
          },
        },
      );

      void logInfo('SDRs fetched.', source, { userId, count: data?.length });

      return { success: true, data: data ?? [] };
    } catch (error) {
      return handleError<SdrInfo[]>(
        'Error fetching SDRs.',
        error,
        source,
        { userId },
      );
    }
  }, [handleError]);

  const getOrganizationsAsync = useCallback(async (): Promise<ApiResponse<OrganizationInfo[]>> => {
    const source = 'Dashboard.getOrganizationsAsync';

    // Serve from in-memory cache when fresh.
    if (orgCache !== null && Date.now() - orgCache.fetchedAt < ORG_CACHE_TTL_MS) {
      return { success: true, data: orgCache.data };
    }

    // De-duplicate concurrent callers (page mount + session enrichment).
    if (pendingOrgsPromise !== null) {
      return await pendingOrgsPromise;
    }

    pendingOrgsPromise = (async (): Promise<ApiResponse<OrganizationInfo[]>> => {
      try {
        const url = microServicesRoutes.edge.get.organizations.getAllActiveOrganizations;

        type ServerDto =
          | OrganizationInfo[]
          | { data?: OrganizationInfo[]; activeOrganizations?: OrganizationInfo[] };

        const response = await edgeApi.get<ServerDto>(url);
        const list: OrganizationInfo[] = Array.isArray(response.data)
          ? response.data
          : response.data?.data ?? response.data?.activeOrganizations ?? [];

        orgCache = { fetchedAt: Date.now(), data: list };

        void logInfo('Organizations fetched.', source, { count: list.length });

        return { success: true, data: list };
      } catch (error) {
        return handleError<OrganizationInfo[]>(
          'Error fetching organizations.',
          error,
          source,
        );
      }
    })();

    try {
      return await pendingOrgsPromise;
    } finally {
      pendingOrgsPromise = null;
    }
  }, [handleError]);

  const getContactByIdAsync = useCallback(async (
    contactId: string,
  ): Promise<ApiResponse<ContactInfo>> => {
    const source = 'Dashboard.getContactByIdAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.get.contact.getById}/${contactId}`;

      const response = await contactsApi.get<ContactInfo>(requestUrl);

      void logInfo('Contact info fetched.', source, { contactId });

      return { success: true, data: response.data };
    } catch (error) {
      return handleError<ContactInfo>(
        'Error fetching contact info.',
        error,
        source,
        { contactId },
      );
    }
  }, [handleError]);

  const downloadReportAsync = useCallback(async (
    callSessionId: string,
    reportType: 'raw' | 'compact',
    organizationId?: string,
  ): Promise<ApiResponse<undefined>> => {
    const source = 'Dashboard.downloadReportAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.get.callSession.getReport}/${callSessionId}`;

      const params: Record<string, string> = { type: reportType };
      if (reportType === 'raw' && organizationId !== undefined && organizationId !== '') {
        params.organizationId = organizationId;
      }

      const response = await contactsApi.get<Blob>(requestUrl, {
        params,
        responseType: 'blob',
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report-${callSessionId}-${reportType}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      void logInfo('Report downloaded.', source, { callSessionId, reportType });

      return { success: true, data: undefined };
    } catch (error) {
      return handleError<undefined>(
        'Error downloading report.',
        error,
        source,
        { callSessionId, reportType },
      );
    }
  }, [handleError]);

  const getBuildMetricsAsync = useCallback(async (
    callSessionId: string,
  ): Promise<ApiResponse<BuildMetrics>> => {
    const source = 'Dashboard.getBuildMetricsAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.get.metrics.buildMetrics}/${callSessionId}`;
      const response = await contactsApi.get<BuildMetrics>(requestUrl);

      return { success: true, data: response.data };
    } catch (error) {
      return handleError<BuildMetrics>(
        'Error fetching build metrics.',
        error,
        source,
        { callSessionId },
      );
    }
  }, [handleError]);

  const getDispositionMetricsAsync = useCallback(async (
    callSessionId: string,
  ): Promise<ApiResponse<DispositionMetrics>> => {
    const source = 'Dashboard.getDispositionMetricsAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.get.metrics.buildDispositionMetrics}/${callSessionId}`;
      const response = await contactsApi.get<DispositionMetrics>(requestUrl);

      return { success: true, data: response.data };
    } catch (error) {
      return handleError<DispositionMetrics>(
        'Error fetching disposition metrics.',
        error,
        source,
        { callSessionId },
      );
    }
  }, [handleError]);

  const getOverviewKpisAsync = useCallback(async (
    params: OverviewQueryParams,
  ): Promise<ApiResponse<unknown>> => {
    const source = 'Dashboard.getOverviewKpisAsync';
    try {
      const response = await contactsApi.get(
        microServicesRoutes.contacts.get.dashboard.overviewKpis,
        { params },
      );

      void logInfo('Overview KPIs fetched.', source, {
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
      });

      return { success: true, data: response.data };
    } catch (error) {
      return handleError(
        'Error fetching overview KPIs.',
        error,
        source,
        { dateFrom: params.dateFrom, dateTo: params.dateTo },
      );
    }
  }, [handleError]);

  const getOverviewLeaderboardAsync = useCallback(async (
    params: OverviewQueryParams,
  ): Promise<ApiResponse<unknown>> => {
    const source = 'Dashboard.getOverviewLeaderboardAsync';
    try {
      const response = await contactsApi.get(
        microServicesRoutes.contacts.get.dashboard.overviewLeaderboard,
        { params },
      );

      void logInfo('Overview leaderboard fetched.', source, {
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
      });

      return { success: true, data: response.data };
    } catch (error) {
      return handleError(
        'Error fetching overview leaderboard.',
        error,
        source,
        { dateFrom: params.dateFrom, dateTo: params.dateTo },
      );
    }
  }, [handleError]);

  const getHubspotJobs = useCallback(async (
    organizationId: string,
    filters: DashboardFilters,
    page: string,
    pageLimit: string,
  ) => {
    const fromDate = filters.dateRange.from;
    const toDate = filters.dateRange.to;

    try {
      const params: Record<string, string | Date> = {
        page,
        pageLimit
      };

      if (fromDate !== undefined && fromDate !== null) {
        params.startRangeDate = fromDate instanceof Date
          ? fromDate
          : new Date(fromDate);
      }
      if (toDate !== undefined && toDate !== null) {
        params.endRangeDate = toDate instanceof Date
          ? toDate
          : new Date(toDate);
      }

      const requestUrl = `${microServicesRoutes.sync.get.hubspot.dashboard}/${organizationId}`;

      const response = await syncApi.get<PaginatedWithMetricsResponse<IntegrationJob>>(requestUrl,
        { params }
      );

      void logInfo('Hubspot jobs fetched.', 'Dashboard.getHubspotJobs', { organizationId, toDate, fromDate, page }, false);

      return {
        success: true as const,
        data: response.data,
        totalPages: response.data?.totalPages,
        totalCount: response.data?.totalCount,
        pageNumber: response.data?.pageNumber,
      };
    } catch (error) {
      return handleError<PaginatedWithMetricsResponse<IntegrationJob>>('Error fetching Hubspot jobs.', error, 'Dashboard.getHubspotJobs', { organizationId, toDate, fromDate, page });
    }
  }, [handleError]);

  const getOutreachJobs = useCallback(async (
    organizationId: string,
    filters: DashboardFilters,
    page: string,
    pageLimit: string,
  ) => {
    const fromDate = filters.dateRange.from;
    const toDate = filters.dateRange.to;

    try {
      const params: Record<string, string | Date> = {
        page,
        pageLimit
      };

      if (fromDate !== undefined && fromDate !== null) {
        params.startRangeDate = fromDate instanceof Date
          ? fromDate
          : new Date(fromDate);
      }
      if (toDate !== undefined && toDate !== null) {
        params.endRangeDate = toDate instanceof Date
          ? toDate
          : new Date(toDate);
      }

      const requestUrl = `${microServicesRoutes.sync.get.outreach.dashboard}/${organizationId}`;

      const response = await syncApi.get<PaginatedWithMetricsResponse<IntegrationJob>>(requestUrl,
        { params }
      );

      void logInfo('Outreach jobs fetched.', 'Dashboard.getOutreachJobs', { organizationId, toDate, fromDate, page }, false);

      return {
        success: true as const,
        data: response.data,
        totalPages: response.data?.totalPages,
        totalCount: response.data?.totalCount,
        pageNumber: response.data?.pageNumber,
      };
    } catch (error) {
      return handleError<PaginatedWithMetricsResponse<IntegrationJob>>('Error fetching Outreach jobs.', error, 'Dashboard.getOutreachJobs', { organizationId, toDate, fromDate, page });
    }
  }, [handleError]);

  const getSalesforceJobs = useCallback(async (
    organizationId: string,
    filters: DashboardFilters,
    page: string,
    pageLimit: string,
  ) => {
    const fromDate = filters.dateRange.from;
    const toDate = filters.dateRange.to;

    try {
      const params: Record<string, string | Date> = {
        page,
        pageLimit
      };

      if (fromDate !== undefined && fromDate !== null) {
        params.startRangeDate = fromDate instanceof Date
          ? fromDate
          : new Date(fromDate);
      }
      if (toDate !== undefined && toDate !== null) {
        params.endRangeDate = toDate instanceof Date
          ? toDate
          : new Date(toDate);
      }

      const requestUrl = `${microServicesRoutes.sync.get.salesforce.dashboard}/${organizationId}`;

      const response = await syncApi.get<PaginatedWithMetricsResponse<IntegrationJob>>(requestUrl,
        { params }
      );

      void logInfo('Salesforce jobs fetched', 'Dashboard.getSalesforceJobs', { organizationId, toDate, fromDate, page }, false);

      return {
        success: true as const,
        data: response.data,
        totalPages: response.data?.totalPages,
        totalCount: response.data?.totalCount,
        pageNumber: response.data?.pageNumber,
      };
    } catch (error) {
      return handleError<PaginatedWithMetricsResponse<IntegrationJob>>('Error fetching Salesforce jobs', error, 'Dashboard.getSalesforceJobs', { organizationId, toDate, fromDate, page });
    }
  }, [handleError]);

  const retryHubspotJob = useCallback( async (
    jobId: string,
    includeSkipped = false,
  ): Promise<ApiResponse<Job>> => {
    try {
      const requestUrl = `${microServicesRoutes.sync.post.hubspot.retryJob}/${jobId}`;
      const response = await syncApi.post<Job>(
        requestUrl,
        null,
        { params: { includeSkipped } },
      );

      void logInfo('Hubspot job retried', 'Dashboard.retryHubspotJob', { jobId, includeSkipped }, false);

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return handleError<Job>('Error retrying Hubspot job', error, 'Dashboard.retryHubspotJob', { jobId, includeSkipped });
    }
  }, [handleError]);

  const retryOutreachJob = useCallback( async (
    jobId: string,
  ): Promise<ApiResponse<Job>> => {
    try {
      const requestUrl = `${microServicesRoutes.sync.post.outreach.retryJob}/${jobId}`;
      const response = await syncApi.post<Job>(requestUrl);

      void logInfo('Outreach job retried', 'Dashboard.retryOutreachJob', { jobId }, false);

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return handleError<Job>('Error retrying Outreach job', error, 'Dashboard.retryOutreachJob', { jobId });
    }
  }, [handleError]);

  const retrySalesforceJob = useCallback( async (
    jobId: string,
  ): Promise<ApiResponse<Job>> => {
    try {
      const requestUrl = `${microServicesRoutes.sync.post.salesforce.retryJob}/${jobId}`;
      const response = await syncApi.post<Job>(requestUrl);

      void logInfo('Salesforce job retried', 'Dashboard.retrySalesforceJob', { jobId }, false);

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return handleError<Job>('Error retrying Salesforce job', error, 'Dashboard.retrySalesforceJob', { jobId });
    }
  }, [handleError]);

  return useMemo(() => ({
    getSessionsAsync,
    getSessionDetailsAsync,
    getRecordingsAsync,
    getSdrsAsync,
    getOrganizationsAsync,
    getContactByIdAsync,
    downloadReportAsync,
    getBuildMetricsAsync,
    getDispositionMetricsAsync,
    getOverviewKpisAsync,
    getOverviewLeaderboardAsync,
    getHubspotJobs,
    getOutreachJobs,
    getSalesforceJobs,
    retryHubspotJob,
    retryOutreachJob,
    retrySalesforceJob
  }), [
    getSessionsAsync,
    getSessionDetailsAsync,
    getRecordingsAsync,
    getSdrsAsync,
    getOrganizationsAsync,
    getContactByIdAsync,
    downloadReportAsync,
    getBuildMetricsAsync,
    getDispositionMetricsAsync,
    getOverviewKpisAsync,
    getOverviewLeaderboardAsync,
    getHubspotJobs,
    getOutreachJobs,
    getSalesforceJobs,
    retryHubspotJob,
    retryOutreachJob,
    retrySalesforceJob
  ]);
}